import React, { useEffect, useRef } from 'react';
import Quill from 'quill';
import {
  registerSmartArtQuillFormats,
  SMART_ART_TOOLBAR_CONFIG,
} from './smartArtQuillConfig';

/**
 * Rich-text editor for a single SmartArt node. Mounted only while that node is
 * being edited; its toolbar lives in the global #quillToolbar container, the
 * same slot the textbox editors use. Commits exactly once — on blur, on
 * Enter/Escape, or on unmount (outside click) — via onCommit({ text, delta }).
 */
const SmartArtNodeEditor = ({ initialDelta, initialText, onCommit }) => {
  const hostRef = useRef(null);
  const onCommitRef = useRef(onCommit);
  onCommitRef.current = onCommit;
  const commitRef = useRef(null);
  // captured once — the editor is seeded on mount and owns the content after that
  const initialRef = useRef({ delta: initialDelta, text: initialText });

  useEffect(() => {
    registerSmartArtQuillFormats();
    const globalToolbarContainer = document.querySelector('#quillToolbar');
    const quill = new Quill(hostRef.current, {
      theme: 'snow',
      modules: { toolbar: SMART_ART_TOOLBAR_CONFIG },
    });
    const { delta, text } = initialRef.current;
    quill.setContents(delta || { ops: [{ insert: `${text || ''}\n` }] });

    const toolbar = quill.getModule('toolbar');
    // keep editor focus while clicking toolbar buttons (same trick as Editor.js)
    const swallowMouseDown = (e) => e.preventDefault();
    toolbar.container.addEventListener('mousedown', swallowMouseDown, false);
    // the toolbar is outside the SmartArt container — mark it so outside-click
    // handlers don't treat toolbar clicks as "clicked away"
    toolbar.container.setAttribute('data-smartart-node-toolbar', 'true');
    if (globalToolbarContainer) globalToolbarContainer.appendChild(toolbar.container);

    let committed = false;
    const commit = () => {
      if (committed) return;
      committed = true;
      onCommitRef.current({
        delta: quill.getContents(),
        text: quill.getText().replace(/\n+$/, '').trim(),
      });
    };
    commitRef.current = commit;

    // Intentionally NO commit-on-blur here. Clicking the toolbar (which lives in
    // #quillToolbar, outside the editor) briefly blurs the editor; committing on
    // that blur would unmount this component and discard Quill's pending
    // "format-on-cursor" state, so a format chosen with no text selected would
    // never reach the text the user types next. Editing is instead finished by
    // Enter/Escape (handleKeyDown), by clicking away (SmartArtObject's outside
    // handler → editingNodeId=null → unmount → the cleanup commit below), or by
    // any other unmount (editing another node, slide change, deselect).
    quill.focus();
    quill.setSelection(Math.max(quill.getLength() - 1, 0), 0, 'silent');

    return () => {
      commit(); // unmounted without blur (outside click, slide change) — still save
      toolbar.container.removeEventListener('mousedown', swallowMouseDown, false);
      toolbar.container.remove();
    };
  }, []);

  const handleKeyDown = (e) => {
    e.stopPropagation(); // don't trigger slide-level shortcuts while typing
    if (e.key === 'Escape' || (e.key === 'Enter' && !e.shiftKey)) {
      e.preventDefault();
      commitRef.current?.();
    }
  };

  return <div ref={hostRef} onKeyDown={handleKeyDown} />;
};

export default SmartArtNodeEditor;
